import { KeyboardDoubleArrowRight } from '@locus-taxy/encore-mui/icons';
import { Stack } from '@locus-taxy/encore-mui';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from '@locus-taxy/encore-i18n-toolkit';
import { PageHeader } from '@locus-taxy/encore-ui-toolkit';
import type { CustomAppEntityHeaderProps } from './types';
import { usePreserveSearchParams } from '../../hooks/usePreserveSearchParams';
import { buildClientPrefixedPath } from '../../utils/url';
import { useClientId } from '../../hooks/useClientId';
import MoreActionsMenu from './MoreActionsMenu';
import EntityStatusChips from './EntityStatusChips';
import EntityTeamChips from './EntityTeamChips';
import { useRouteContext } from '../../hooks/useRouteContext';
import { useTeams } from '../../api/endpoints/useTeams';

export default function CustomAppEntityHeader({
  entityName,
  entityId,
  entityNameToShow,
  entityTitle,
  actions,
  onActionClick,
  onViewSchema,
  entityData,
}: Readonly<CustomAppEntityHeaderProps>) {
  const navigate = useNavigate();
  const clientId = useClientId();
  const { data: teams = [] } = useTeams(clientId);
  const showOutboundButton =
    entityName === 'b2cDeliveryTour' && entityData.tourStatus === 'STAGED';
  const { entity, page } = useRouteContext();
  const handleOutboundClick = () => {
    const url = buildClientPrefixedPath(
      `/entities/${entityName}/${entityId}/outbound`,
      clientId
    );
    navigate(url);
  };
  const { getUrlWithSearchParams } = usePreserveSearchParams();
  const { t } = useTranslation();

  const handleBackClick = () => {
    const entityUrl = getUrlWithSearchParams(
      buildClientPrefixedPath(`/entities/${entityName}`, clientId)
    );
    navigate(entityUrl);
  };

  const rightSlot = <MoreActionsMenu onViewSchema={onViewSchema} t={t} />;

  // Breadcrumbs
  const homeUrl = getUrlWithSearchParams(buildClientPrefixedPath('', clientId));
  const entityUrl = getUrlWithSearchParams(
    buildClientPrefixedPath(`/entities/${entityName}`, clientId)
  );

  const breadcrumbs = [
    { id: 'home', label: 'Home', href: homeUrl },
    { id: 'entity', label: entityTitle || entityName, href: entityUrl },
  ];

  // Actions
  const dropdownActions = (actions || []).map(action => ({
    type: 'item' as const,
    id: action.id,
    label: action.label,
    onClick: () => onActionClick(action),
  }));

  const flatActions = showOutboundButton
    ? [
        {
          id: 'outbound',
          label: 'Outbound',
          icon: <KeyboardDoubleArrowRight />,
          variant: 'outline' as const,
          onClick: handleOutboundClick,
        },
      ]
    : undefined;

  // Status Chip and Team Chips
  const status = entityData?.status as string;
  const subStatus = entityData?.subStatus as string;
  const teamIds = entityData?.teamIds as string[] | undefined;

  const titleAdornment = (
    <Stack direction="row" gap={1} alignItems="center">
      <EntityTeamChips teamIds={teamIds} teams={teams} />
      <EntityStatusChips status={status} subStatus={subStatus} />
    </Stack>
  );

  return (
    <PageHeader
      title={entityNameToShow}
      titleAdornment={titleAdornment}
      breadcrumbs={breadcrumbs}
      onBackClick={handleBackClick}
      flatActions={flatActions}
      dropdownActions={dropdownActions}
      routeContext={{
        entity: entity ?? entityName,
        page: page ?? 'details',
      }}
      slots={{
        right: rightSlot,
      }}
      labels={{
        actionsLabel: t('schemaModal.actions'),
        otherActionsLabel: t('schemaModal.actions'),
      }}
    />
  );
}
